/**
 * Category Analysis Utility
 * Analyzes competitor categories and suggests the best category for a business
 * 
 * @author Visibeen Team - 2024
 */

import logger from './logger';

/**
 * Category Equivalence Mapping
 * Groups of categories that should be treated as equivalent/same
 * Each array contains categories that are semantically the same
 */
const CATEGORY_EQUIVALENCE_GROUPS = [
    // Real Estate related
    ['property consultant', 'real estate consultant', 'real estate agency', 'real estate agent', 'property dealer', 'property agent'],

    // Electronics related
    ['electronics retail and repair shop', 'electronics store', 'electronics shop', 'consumer electronics store', 'electronics retailer'],

    // Restaurant/Food related
    ['restaurant', 'dining establishment', 'eatery', 'food establishment'],
    ['cafe', 'coffee shop', 'coffee house', 'coffeehouse'],
    ['fast food restaurant', 'quick service restaurant', 'fast food'],

    // Medical/Health related
    ['doctor', 'physician', 'medical practitioner', 'general practitioner'],
    ['hospital', 'medical center', 'medical centre', 'healthcare facility'],
    ['pharmacy', 'drugstore', 'chemist', 'medical store'],
    ['dentist', 'dental clinic', 'dental office', 'dental practice'],

    // Legal related
    ['lawyer', 'attorney', 'law firm', 'legal services', 'advocate'],

    // Automotive related
    ['car dealer', 'auto dealer', 'automobile dealer', 'car dealership', 'auto dealership'],
    ['car repair', 'auto repair', 'automobile repair', 'car mechanic', 'auto mechanic', 'mechanic'],
    ['car wash', 'auto wash', 'automobile wash'],

    // Retail related
    ['clothing store', 'apparel store', 'fashion store', 'garment store', 'clothes shop'],
    ['grocery store', 'supermarket', 'grocery', 'food store'],
    ['convenience store', 'general store', 'corner store'],
    ['jewelry store', 'jewellery store', 'jeweler', 'jeweller'],

    // Beauty related
    ['beauty salon', 'beauty parlour', 'beauty parlor', 'beauty shop'],
    ['hair salon', 'hair dresser', 'hairdresser', 'barber shop', 'barber'],
    ['spa', 'wellness center', 'wellness centre', 'day spa'],

    // Education related
    ['school', 'educational institution', 'academy'],
    ['tutor', 'tutoring service', 'coaching center', 'coaching centre'],
    ['gym', 'fitness center', 'fitness centre', 'health club', 'gymnasium'],

    // Professional Services
    ['accountant', 'accounting firm', 'accounting services', 'chartered accountant', 'ca firm'],
    ['consultant', 'consulting firm', 'business consultant', 'management consultant'],
    ['insurance agent', 'insurance agency', 'insurance broker', 'insurance company'],

    // Home Services
    ['plumber', 'plumbing service', 'plumbing contractor'],
    ['electrician', 'electrical contractor', 'electrical service'],
    ['contractor', 'building contractor', 'construction company', 'construction contractor'],
    ['interior designer', 'interior decorator', 'interior design studio'],

    // Technology
    ['computer store', 'computer shop', 'pc store'],
    ['mobile phone store', 'cell phone store', 'phone store', 'mobile shop'],
    ['it services', 'computer repair', 'tech support', 'computer service'],

    // Hospitality
    ['hotel', 'lodging', 'accommodation', 'inn'],
    ['travel agency', 'travel agent', 'tour operator', 'travel company'],
];

/**
 * Build a lookup map for faster equivalence checking
 */
const buildEquivalenceMap = () => {
    const map = new Map();
    CATEGORY_EQUIVALENCE_GROUPS.forEach((group, groupIndex) => {
        group.forEach(category => {
            map.set(category.toLowerCase().trim(), groupIndex);
        });
    });
    return map;
};

const EQUIVALENCE_MAP = buildEquivalenceMap();

/**
 * Check if two categories are equivalent (same or semantically similar)
 * @param {string} category1 - First category name
 * @param {string} category2 - Second category name
 * @returns {boolean} True if categories are equivalent
 */
function areCategoriesEquivalent(category1, category2) {
    if (!category1 || !category2) return false;

    const cat1 = category1.toLowerCase().trim();
    const cat2 = category2.toLowerCase().trim();

    // Exact match
    if (cat1 === cat2) return true;

    // Check if they belong to the same equivalence group
    const group1 = EQUIVALENCE_MAP.get(cat1);
    const group2 = EQUIVALENCE_MAP.get(cat2);

    if (group1 !== undefined && group2 !== undefined && group1 === group2) {
        return true;
    }

    // Fallback: substring matching for partial matches
    if (cat1.includes(cat2) || cat2.includes(cat1)) {
        return true;
    }

    // Additional fuzzy matching: check if key words overlap significantly
    const words1 = cat1.split(/\s+/).filter(w => w.length > 2);
    const words2 = cat2.split(/\s+/).filter(w => w.length > 2);

    if (words1.length > 0 && words2.length > 0) {
        const commonWords = words1.filter(w => words2.some(w2 => w === w2 || w.includes(w2) || w2.includes(w)));
        // If more than half the words match, consider them equivalent
        const matchRatio = commonWords.length / Math.min(words1.length, words2.length);
        if (matchRatio >= 0.5) {
            return true;
        }
    }

    return false;
}

/**
 * Analyze competitor categories and generate category suggestion
 * @param {Array} competitors - Array of competitor objects with category data
 * @param {string|Object} userCategory - User's current business category
 * @returns {Object} Category analysis result with suggestion
 */
export function analyzeCompetitorCategories(competitors, userCategory) {
    if (!Array.isArray(competitors) || competitors.length === 0) {
        logger.warn('[CategoryAnalysis] No competitors provided for analysis');
        return null;
    }

    // Normalize user category to string
    const userCategoryName = normalizeCategory(userCategory);

    if (!userCategoryName) {
        logger.warn('[CategoryAnalysis] User category is empty or invalid');
        return null;
    }

    // Take top 10 competitors (or all if less than 10)
    const topCompetitors = competitors.slice(0, 10);

    // Extract and count categories from competitors
    const categoryCount = {};
    let validCompetitorCount = 0;

    topCompetitors.forEach(competitor => {
        const category = extractCategory(competitor);
        if (category) {
            const normalized = category.toLowerCase().trim();
            categoryCount[normalized] = (categoryCount[normalized] || 0) + 1;
            validCompetitorCount++;
        }
    });

    if (validCompetitorCount === 0) {
        logger.warn('[CategoryAnalysis] No valid categories found in competitors');
        return null;
    }

    // Find the most common category
    const sortedCategories = Object.entries(categoryCount)
        .sort((a, b) => b[1] - a[1]);

    const [topCategoryName, topCategoryCount] = sortedCategories[0];
    const confidence = Math.round((topCategoryCount / validCompetitorCount) * 100);

    // Check if user category matches the top category (using equivalence mapping)
    const userCategoryNormalized = userCategoryName.toLowerCase().trim();
    const matchesCompetitors = areCategoriesEquivalent(userCategoryNormalized, topCategoryName);

    // Format the suggested category nicely (title case)
    const suggestedCategory = formatCategoryName(topCategoryName);

    // Build category statistics
    const competitorStats = {
        total: validCompetitorCount,
        categories: sortedCategories.map(([name, count]) => ({
            name: formatCategoryName(name),
            count,
            percentage: Math.round((count / validCompetitorCount) * 100)
        })),
        topCategory: suggestedCategory,
        topCategoryCount,
        topCategoryPercentage: confidence
    };

    const result = {
        userCategory: userCategoryName,
        suggestedCategory,
        matchesCompetitors,
        confidence,
        competitorStats,
        analysisTimestamp: Date.now(),
        message: generateProfessionalMessage(userCategoryName, suggestedCategory, confidence, validCompetitorCount, matchesCompetitors)
    };

    logger.log('[CategoryAnalysis] Analysis complete:', result);
    return result;
}

/**
 * Extract category from competitor object
 * Handles various data formats from different APIs
 */
function extractCategory(competitor) {
    // Priority order for category extraction
    if (competitor.primaryType) {
        return competitor.primaryType;
    }
    if (competitor.category) {
        return competitor.category;
    }
    if (competitor.categories && Array.isArray(competitor.categories) && competitor.categories.length > 0) {
        return competitor.categories[0];
    }
    if (competitor.types && Array.isArray(competitor.types) && competitor.types.length > 0) {
        // Filter out generic types like 'point_of_interest', 'establishment'
        const genericTypes = ['point_of_interest', 'establishment', 'place', 'store'];
        const specificType = competitor.types.find(t => !genericTypes.includes(t.toLowerCase()));
        return specificType || competitor.types[0];
    }
    return null;
}

/**
 * Normalize category to string format
 */
function normalizeCategory(category) {
    if (!category) return '';
    if (typeof category === 'string') return category.trim();
    if (typeof category === 'object') {
        return category.displayName || category.name || category.title || '';
    }
    return String(category).trim();
}

/**
 * Format category name to Title Case
 */
function formatCategoryName(name) {
    if (!name) return '';
    return name
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Generate professional message for the category task
 */
function generateProfessionalMessage(userCategory, suggestedCategory, confidence, totalCompetitors, matchesCompetitors) {
    if (matchesCompetitors) {
        return {
            title: `✅ Your Category is Optimized`,
            description: `Great news! Your category "${userCategory}" matches what top competitors are using. You're aligned with industry standards.`,
            actionNeeded: false
        };
    }

    // Professional messaging based on confidence level
    let urgency = '';
    let recommendation = '';

    if (confidence >= 70) {
        urgency = 'Strong recommendation';
        recommendation = `${confidence}% of the top ${totalCompetitors} competitors in your area use "${suggestedCategory}". This is the dominant category and switching could significantly improve your visibility.`;
    } else if (confidence >= 50) {
        urgency = 'Recommended';
        recommendation = `${confidence}% of top competitors use "${suggestedCategory}". Consider updating to align with the majority of successful businesses in your area.`;
    } else {
        urgency = 'Consider reviewing';
        recommendation = `While categories vary in your area, "${suggestedCategory}" is used by ${confidence}% of top performers. Review if it better describes your services.`;
    }

    return {
        title: `🔄 Update Category to "${suggestedCategory}"`,
        description: `Your current category "${userCategory}" differs from competitors. ${recommendation}`,
        actionNeeded: true,
        urgency,
        confidence
    };
}

export default {
    analyzeCompetitorCategories,
    areCategoriesEquivalent
};

// Named export for direct import
export { areCategoriesEquivalent };
